// Which recordings a step, a lesson, a unit and the learner's window can play.
// Pure: shared with the native app.
//
// Lesson and unit sets are supersets by construction: they walk the same
// candidate and pool functions the generator does instead of sampling builds.
// `audio-sets.test.ts` builds many seeded lessons and reviews and fails if a
// step plays anything outside its set. Any exercise view that starts playing a
// new recording must be mirrored in `stepAudio`, or offline gating lies.

import type { Course } from "@aybuben/core/content/course.ts";
import { recipeById } from "@aybuben/core/content/course.ts";
import type { LessonDef, UnitDef } from "@aybuben/core/content/types.ts";
import { itemIdOf } from "@aybuben/core/content/types.ts";
import {
	assertNever,
	candidatesOf,
	poolFor,
	type LessonStep,
	type Option,
} from "@aybuben/core/lesson.ts";
import { nextLesson, started } from "@aybuben/core/progress/query.ts";
import type { GameState } from "@aybuben/core/state.ts";

const optionAudio = (o: Option): string[] => [
	...(o.sound ? [o.sound] : []),
	...(o.face.kind === "audio" ? [o.face.asset] : []),
];

const audioOfItems = (course: Course, ids: Iterable<string>): Set<string> => {
	const out = new Set<string>();
	for (const id of ids) {
		const a = course.itemById(id).audio;
		if (a) out.add(a);
	}
	return out;
};

/** Every recording a built step's view can play: prompts, tiles, feedback panels. */
export function stepAudio(step: LessonStep, course: Course): Set<string> {
	const ids: string[] = [];
	const sounds: string[] = [];
	switch (step.kind) {
		case "intro":
			ids.push(itemIdOf(step.key));
			break;
		case "choice":
			// The feedback panel plays the answer's letter, looked up by character.
			ids.push(itemIdOf(step.key), course.views.letterByChar(step.answer).id);
			for (const o of step.options) sounds.push(...optionAudio(o));
			break;
		case "word":
		case "meaning":
			ids.push(step.word.id);
			for (const o of step.options) sounds.push(...optionAudio(o));
			break;
		case "spell":
			ids.push(step.word.id);
			for (const o of step.bank) sounds.push(...optionAudio(o));
			break;
		case "match":
			for (const o of [...step.left, ...step.right]) sounds.push(...optionAudio(o));
			break;
		case "compose":
			if (step.sound) sounds.push(step.sound);
			for (const o of step.bank) sounds.push(...optionAudio(o));
			break;
		case "explain":
			for (const r of step.rows) ids.push(r.itemId);
			break;
		default:
			assertNever(step);
	}
	const out = audioOfItems(course, ids);
	for (const s of sounds) out.add(s);
	return out;
}

export function stepsAudio(steps: readonly LessonStep[], course: Course): Set<string> {
	const out = new Set<string>();
	for (const s of steps) for (const a of stepAudio(s, course)) out.add(a);
	return out;
}

// Per course, so each lesson's set is walked once: `poolFor` with a course-wide
// pool scans every earlier unit, which makes all of them together grow with the
// square of the course. A course is never mutated, and a new release or
// language is a new `Course`, so nothing goes stale.
const lessonCache = new WeakMap<Course, Map<string, ReadonlySet<string>>>();

/** Every recording any build of this lesson can play. Shared: do not mutate. */
export function lessonAudio(course: Course, unit: UnitDef, lesson: LessonDef): ReadonlySet<string> {
	let perCourse = lessonCache.get(course);
	if (!perCourse) lessonCache.set(course, (perCourse = new Map()));
	// Unit and lesson id together: lesson ids are not checked unique on their own.
	const key = `${unit.id}\u0000${lesson.id}`;
	let set = perCourse.get(key);
	if (!set) perCourse.set(key, (set = walkLessonAudio(course, unit, lesson)));
	return set;
}

function walkLessonAudio(course: Course, unit: UnitDef, lesson: LessonDef): Set<string> {
	const items = new Set<string>();
	// A cloze compose step reads the unit's taught forms and practised
	// sentences, and an explain card its whole paradigm, not the drawn item's pool.
	for (const i of course.itemsOf(unit, "teaches")) items.add(i.id);
	for (const i of course.itemsOf(unit, "practises")) items.add(i.id);
	// The bundled recipe table, as `buildLessonTraced` reads it: the set must
	// describe what is built, and the build does not read recipes from the release.
	for (const r of recipeById(lesson.recipe).steps) {
		for (const item of candidatesOf(unit, lesson, course, r)) {
			items.add(item.id);
			for (const id of poolFor(item, unit, course, r)) items.add(id);
		}
	}
	return audioOfItems(course, items);
}

export function unitAudio(course: Course, unit: UnitDef): Set<string> {
	const out = new Set<string>();
	for (const l of unit.lessons) for (const a of lessonAudio(course, unit, l)) out.add(a);
	return out;
}

/** Units in map order. The order comes from the app build, not the release (README). */
export const orderedUnits = (course: Course): UnitDef[] => course.views.units().map((u) => u.def);

/** The unit the map is nudging toward, else the furthest started, else the first. */
export function currentUnitIndex(units: readonly UnitDef[], st: GameState): number {
	const next = nextLesson(st, units);
	if (next) return units.findIndex((u) => u.id === next.unit.id);
	return Math.max(
		0,
		units.findLastIndex((u) => started(st, u)),
	);
}

export interface AudioWindow {
	/** Index of the unit whose recordings block launch. */
	readonly current: number;
	/** Last unit index whose recordings are kept; every unit from 0 to here is. */
	readonly last: number;
}

export function audioWindow(
	units: readonly UnitDef[],
	st: GameState,
	lookaheadUnits: number,
): AudioWindow {
	const current = currentUnitIndex(units, st);
	// Anchored on the furthest unlocked unit as well as on the nudge: content
	// inserted behind a learner nudges them back, yet the unit they had reached
	// is still theirs to play, and reviews draw from every started (so unlocked) unit.
	const furthest = units.findLastIndex((u) => !!st.unlocked[u.id]);
	const last = Math.min(units.length - 1, Math.max(current, furthest) + lookaheadUnits);
	return { current, last };
}

/**
 * Before the account's history is folded in (a new device, or the first launch
 * after a content release discards the local snapshot), progress may read as a
 * beginner's. The window last computed from the full history is then the
 * better guess for the unit to wait on, and the union keeps both downloaded.
 *
 * The further of the two units is waited on: an intact local snapshot can be
 * ahead of the hint (the learner advanced in an offline-only session), and
 * waiting on the older unit would leave the real one to the background. A
 * beginner's snapshot computes unit 0, so the hint wins there on its own.
 */
export function withHint(w: AudioWindow, hint: AudioWindow | null, unitCount: number): AudioWindow {
	if (!hint) return w;
	const end = unitCount - 1;
	return {
		current: Math.min(end, Math.max(w.current, hint.current)),
		last: Math.min(end, Math.max(w.last, hint.last)),
	};
}

/** Equal for two states exactly when their windows are; a change means a pass is due. */
export function windowKey(course: Course, st: GameState, lookaheadUnits: number): string {
	const w = audioWindow(orderedUnits(course), st, lookaheadUnits);
	return `${w.current}:${w.last}`;
}

/** Every unit from the first to the window's end (earlier units feed reviews), plus every letter. */
export function wantedAudio(course: Course, w: AudioWindow): Set<string> {
	const out = new Set<string>();
	for (const u of orderedUnits(course).slice(0, w.last + 1))
		for (const a of unitAudio(course, u)) out.add(a);
	// The Letters tab plays any letter at any time, whatever the window.
	for (const i of course.items) if (i.kind === "letter" && i.audio) out.add(i.audio);
	return out;
}
